/**
 * Company: SPHMedia
 * Description: Custom hook to manage the visibility of the Beyond Words playlist sticky player.
 */

import { RefObject, useEffect, useState } from "react";

type useBeyondWordsPlaylistPlayerStickyData = {
  isInlineVisible: boolean;
  isStickyVisible: boolean;
};

export default function useBeyondWordsPlaylistPlayerSticky(
  playerRef: RefObject<HTMLElement | null>,
): useBeyondWordsPlaylistPlayerStickyData {
  const [inlinePastViewport, setInlinePastViewport] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(true);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true,
      once: true,
    });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const el = playerRef.current;
    if (!el) return;

    // Read the CSS variable at effect time as a plain px value
    const headerHeight =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--headerHeight",
        ),
      ) || 64;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!hasScrolled) return;

        if (entry.isIntersecting) {
          setInlinePastViewport(false);
          return;
        }

        // Only treat as "past viewport" if element exited above (scrolled down past it)
        // entry.boundingClientRect.top < 0 means element is above the viewport
        const exitedAbove = entry.boundingClientRect.top < 0;
        setInlinePastViewport(exitedAbove);
      },
      {
        threshold: 0,
        rootMargin: `-${headerHeight}px 0px 0px 0px`,
      },
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [playerRef, hasScrolled]);

  return {
    isInlineVisible: !inlinePastViewport,
    isStickyVisible: inlinePastViewport && hasScrolled,
  };
}
